import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconAlertTriangle, IconBrandInstagram, IconBrandMeta, IconChartBar, IconCheck, IconMessageCircle, IconSend } from '@tabler/icons-react';
import { Anchor, Core, Glow, Pill, Sparks, Wires, ambience, blink, charge, entrance, flare, gsap, travel, useSceneAnimation } from '../../../../components/Scene';
import './styles.scss';

// Top to bottom: the Instagram profile → Meta's login, the focal point → what the connection lets
// the Studio do. Wires leave the core at its foot and fan out to the three permissions.
const WIRES = [
  { id: 'account', tone: 'warm', d: 'M180 94 V150', joints: [[180, 94], [180, 150]] },
  { id: 'comments', d: 'M152 234 V262 Q152 270 144 270 H78 Q70 270 70 278 V298', joints: [[152, 234], [70, 298]] },
  { id: 'messages', d: 'M180 234 V298', joints: [[180, 234], [180, 298]] },
  { id: 'insights', d: 'M208 234 V262 Q208 270 216 270 H282 Q290 270 290 278 V298', joints: [[208, 234], [290, 298]] },
];
const SPARKS = [[140, 170], [222, 164], [160, 250], [204, 256], [118, 214]];
const NONE = [];
const PERMISSIONS = ['comments', 'messages', 'insights'];
const FIRST_PERMISSION = 1.15;
const STEP = 0.85;

function build(q, { still }, missing) {
  const seal = q('[data-seal]');
  const granted = PERMISSIONS.filter(id => !missing.includes(id));
  const rings = granted.map(id => q(`[data-ring="${id}"]`)[0]);
  // The composed frame: what was granted lit, and how it ended.
  if (still) {
    gsap.set(rings, { opacity: 0.9 });
    return;
  }
  ambience(q);
  const intro = entrance(q);
  gsap.set(seal, { opacity: 0, scale: 0.8 });
  const loop = gsap.timeline({ repeat: -1, repeatDelay: 0.4, delay: intro.duration() - 0.2 });
  // The profile goes through Meta's login…
  flare(loop, q, 'account', 0, 0.5);
  blink(loop, q, ['account-0'], 0.12);
  travel(loop, q, 'account', 0.15);
  blink(loop, q, ['account-1'], 0.82);
  charge(loop, q, 0.85);
  // …which grants each permission, and they stay lit…
  granted.forEach((id, index) => {
    const at = FIRST_PERMISSION + index * STEP;
    blink(loop, q, [`${id}-0`], at);
    travel(loop, q, id, at + 0.03, 0.65);
    blink(loop, q, [`${id}-1`], at + 0.66);
    loop.fromTo(q(`[data-ring="${id}"]`), { opacity: 0 }, { opacity: 0.9, duration: 0.22, ease: 'power2.out' }, at + 0.66);
  });
  // …until the profile is connected. A long hold, so it reads as the end of the story.
  const done = FIRST_PERMISSION + granted.length * STEP + 0.3;
  loop.to(seal, { opacity: 1, scale: 1, duration: 0.4, ease: 'back.out(2)' }, done);
  loop.to([...rings, ...seal], { opacity: 0, duration: 0.4, ease: 'power2.inOut' }, done + 1.9);
}

// The official connection as a story. Decorative: its host carries the description. `missing` lists
// the permissions left out: they stay dark, on a dashed wire, and the story ends saying so. It is read
// when the scene mounts, so a host whose list can change gives the scene a `key` from it.
export default function ConnectScene({ handle, picture, missing = NONE }) {
  const { t } = useTranslation('studioAutomations');
  const stage = useRef(null);
  useSceneAnimation(stage, (q, conditions) => build(q, conditions, missing));
  const wires = WIRES.map(wire => (missing.includes(wire.id) ? { ...wire, dashed: true } : wire));
  const pillClass = id => (missing.includes(id) ? 'studio-connect-art__left-out' : '');
  return <div className="studio-feature-art studio-connect-art" aria-hidden="true">
    <div className="studio-feature-art__viewport">
      <div className="studio-feature-art__stage" ref={stage}>
        <Glow x={120} y={70} tone="warm" />
        <Glow x={236} y={300} tone="cool" />
        <Glow x={180} y={190} tone="violet" size={130} />
        <Wires wires={wires} />
        <Sparks points={SPARKS} />
        <Anchor x={180} y={64}>
          <div className="studio-connect-art__profile is-warm" data-enter>
            <span className="studio-connect-art__avatar"><span className="studio-connect-art__avatar-face">
              {picture?.url ? <img src={picture.url} alt="" /> : <IconBrandInstagram />}
            </span></span>
            <span className="studio-connect-art__who">
              <span className="studio-connect-art__handle">{handle ? `@${handle}` : t('scenePlaceholder')}</span>
              <span className="studio-connect-art__network"><IconBrandInstagram />Instagram</span>
            </span>
            <span className="studio-feature-art__ring" data-ring="account" />
          </div>
        </Anchor>
        <Anchor x={190} y={122} edge="start"><span className="studio-connect-art__via" data-enter>{t('sceneMeta')}</span></Anchor>
        <Anchor x={180} y={192}><Core icon={IconBrandMeta} /></Anchor>
        <Anchor x={70} y={312}><Pill id="comments" icon={IconMessageCircle} className={pillClass('comments')}>{t('capability_comments')}</Pill></Anchor>
        <Anchor x={180} y={312}><Pill id="messages" icon={IconSend} className={pillClass('messages')}>{t('channel_direct_messages')}</Pill></Anchor>
        <Anchor x={290} y={312}><Pill id="insights" icon={IconChartBar} className={pillClass('insights')}>{t('capability_insights')}</Pill></Anchor>
        <Anchor x={180} y={358}>{missing.length
          ? <span className="studio-feature-art__badge is-warm" data-seal><IconAlertTriangle />{t('sceneMissing')}</span>
          : <span className="studio-feature-art__badge is-cool" data-seal><IconCheck />{t('sceneConnected')}</span>}</Anchor>
      </div>
    </div>
  </div>;
}
